import { Controller } from "@hotwired/stimulus"

/**
 * TTS Generator Controller
 *
 * Handles the modal, voice preview, and TTS generation API calls
 * for the Shared::TtsGeneratorComponent.
 */
export default class extends Controller {
  static targets = [
    "modal",
    "panel",
    "voiceSelect",
    "voiceDescription",
    "modelSelect",
    "textPreview",
    "charCount",
    "costEstimate",
    "previewButton",
    "previewAudio",
    "generateButton",
    "statusContainer",
    "status",
    "statusText",
    "spinner"
  ]

  static values = {
    url: String,
    textSource: String,
    audioField: String,
    contextType: String,
    contextIndex: Number
  }

  // Cost per 1000 characters by model
  static COST_RATES = {
    "eleven_v3": 0.30,
    "eleven_multilingual_v2": 0.30,
    "eleven_turbo_v2_5": 0.18,
    "eleven_flash_v2_5": 0.10
  }

  connect() {
    this.isGenerating = false
    this.handleBackdropClick = this.handleBackdropClick.bind(this)
    this.handleEscape = this.handleEscape.bind(this)
  }

  disconnect() {
    this.stopPreview()
  }

  openModal() {
    this.loadSourceText()
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.handleEscape)
  }

  closeModal() {
    if (this.isGenerating) return

    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.handleEscape)
    this.stopPreview()
    this.hideStatus()
  }

  handleBackdropClick(event) {
    if (this.hasPanelTarget && !this.panelTarget.contains(event.target)) {
      this.closeModal()
    }
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.closeModal()
    }
  }

  loadSourceText() {
    const sourceElement = document.querySelector(this.textSourceValue)
    if (!sourceElement) {
      this.textPreviewTarget.innerHTML = '<em class="text-red-500">Source element not found</em>'
      this.charCountTarget.textContent = "0 characters"
      return
    }

    const text = sourceElement.value || sourceElement.textContent || ""
    const trimmedText = text.trim()

    if (trimmedText) {
      this.textPreviewTarget.textContent = trimmedText
      this.charCountTarget.textContent = `${trimmedText.length} characters`
      this.updateCostEstimate(trimmedText.length)
    } else {
      this.textPreviewTarget.innerHTML = '<em class="text-gray-400">No text available</em>'
      this.charCountTarget.textContent = "0 characters"
      this.costEstimateTarget.textContent = "—"
    }
  }

  voiceChanged() {
    const selected = this.voiceSelectTarget.selectedOptions[0]
    if (selected && this.hasVoiceDescriptionTarget) {
      const description = selected.dataset.description || ""
      this.voiceDescriptionTarget.textContent = description
    }
    this.stopPreview()
  }

  updateCostEstimate(charCount) {
    if (!charCount || charCount === 0) {
      this.costEstimateTarget.textContent = "—"
      return
    }

    const modelId = this.modelSelectTarget.value
    const rate = this.constructor.COST_RATES[modelId] || 0.30
    const cost = (charCount / 1000 * rate).toFixed(4)
    this.costEstimateTarget.textContent = `~$${cost}`
  }

  async playPreview() {
    const selected = this.voiceSelectTarget.selectedOptions[0]
    const previewUrl = selected?.dataset.previewUrl

    if (!previewUrl) {
      this.showStatus("No preview available", "warning")
      return
    }

    // Stop any current preview
    this.stopPreview()

    try {
      this.previewButtonTarget.disabled = true
      this.previewAudioTarget.src = previewUrl
      await this.previewAudioTarget.play()

      this.previewAudioTarget.onended = () => {
        this.previewButtonTarget.disabled = false
      }
    } catch (error) {
      console.error("Preview playback failed:", error)
      this.previewButtonTarget.disabled = false
      this.showStatus("Preview playback failed", "error")
    }
  }

  stopPreview() {
    if (this.hasPreviewAudioTarget) {
      this.previewAudioTarget.pause()
      this.previewAudioTarget.currentTime = 0
    }
    if (this.hasPreviewButtonTarget) {
      this.previewButtonTarget.disabled = false
    }
  }

  async generate() {
    if (this.isGenerating) return

    const sourceElement = document.querySelector(this.textSourceValue)
    const text = (sourceElement?.value || sourceElement?.textContent || "").trim()

    if (!text) {
      this.showStatus("No text to convert", "error")
      return
    }

    if (text.length > 5000) {
      this.showStatus("Text exceeds 5000 character limit", "error")
      return
    }

    this.isGenerating = true
    this.generateButtonTarget.disabled = true
    this.showStatus("Generating audio...", "loading")

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const voiceProfileId = this.voiceSelectTarget.value
      const modelId = this.modelSelectTarget.value

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          text: text,
          voice_profile_id: voiceProfileId,
          model_id: modelId,
          context_type: this.contextTypeValue,
          context_index: this.contextIndexValue
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.showStatus("Audio generated successfully!", "success")
        this.updateAudioField(data.audio_url)

        // Close modal after brief delay to show success
        setTimeout(() => {
          this.closeModal()
          // Optionally reload to show changes
          if (data.reload) {
            window.location.reload()
          }
        }, 1000)
      } else {
        this.showStatus(data.error || "Generation failed", "error")
      }
    } catch (error) {
      console.error("TTS generation failed:", error)
      this.showStatus("Network error occurred", "error")
    } finally {
      this.isGenerating = false
      this.generateButtonTarget.disabled = false
    }
  }

  updateAudioField(audioUrl) {
    // Find the audio field by name and update it
    const audioField = document.querySelector(`[name="${this.audioFieldValue}"]`)
    if (audioField) {
      audioField.value = audioUrl
      // Trigger change event for any listeners
      audioField.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  showStatus(message, type = "info") {
    this.statusContainerTarget.classList.remove("hidden")

    // Style based on type
    const statusClasses = {
      loading: "bg-indigo-50 text-indigo-700",
      success: "bg-green-50 text-green-700",
      error: "bg-red-50 text-red-700",
      warning: "bg-yellow-50 text-yellow-700",
      info: "bg-gray-50 text-gray-700"
    }

    this.statusTarget.className = `flex items-center gap-2 px-3 py-2 rounded-md ${statusClasses[type] || statusClasses.info}`
    this.statusTextTarget.textContent = message

    // Show/hide spinner
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("hidden", type !== "loading")
    }
  }

  hideStatus() {
    this.statusContainerTarget.classList.add("hidden")
  }

  async toggleFavorite(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const url = button.dataset.favoriteUrl

    if (!url) {
      console.error("No favorite URL provided")
      return
    }

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Toggle the star visual state
        const svg = button.querySelector("svg")
        if (data.favorited) {
          svg.setAttribute("fill", "currentColor")
          button.classList.remove("text-gray-300")
          button.classList.add("text-yellow-400")
          button.title = "Remove from favorites"
        } else {
          svg.setAttribute("fill", "none")
          button.classList.remove("text-yellow-400")
          button.classList.add("text-gray-300")
          button.title = "Add to favorites"
        }
      } else {
        console.error("Failed to toggle favorite:", data.error)
      }
    } catch (error) {
      console.error("Toggle favorite failed:", error)
    }
  }
}
